import { Pagination } from './../function/paginate';
import { ContactListMapItem, ContactListPageSearchResults } from './../typings/backend-models';
import cardsPagination from './pagination';

const getData = async (url: string) => {
    const res = await fetch(url).then((response) => response.json());
    return res;
};

const createListItem = (properties: ContactListMapItem) => {
    const { name, title, phoneNumber, email, imageUrl, contentUrl } = properties;

    const articleEl = document.createElement('article');
    articleEl.classList.add('contact');
    if (imageUrl) {
        articleEl.classList.add('contact--has-image');
    }

    const nameMarkup = contentUrl
        ? `<a href="${contentUrl}" class="contact__name contact__name--link"><h3>${name}</h3></a>`
        : `<h3 class="contact__name">${name}</h3>`;

    const content = `
        ${imageUrl ? `
        <div class="contact__image ratio ratio--1-1">
            <img src="${imageUrl}" alt="${name}" class="ratio__item ratio__item--cover" loading="lazy" />
        </div>
        ` : ''}
        <div class="contact__content">
            ${nameMarkup}
            ${title ? `<p class="contact__position">${title}</p>` : ''}
            ${phoneNumber ? `<p class="contact__phone"><a href="tel:${phoneNumber}">${phoneNumber}</a></p>` : ''}
            ${email ? `<p class="contact__email"><a href="mailto:${email}">${email}</a></p>` : ''}
        </div>
    `;
    articleEl.innerHTML = content;
    return articleEl;
};

const createList = (elements: ContactListMapItem[]) => {
    const contactList = document.querySelector('.contact-list');
    if (!contactList) return;
    
    // Remove existing contact items
    const existingItems = contactList.querySelectorAll('.contact');
    existingItems.forEach(item => item.remove());

    elements.forEach((element) => {
        const listItem = createListItem(element);
        contactList.appendChild(listItem);
    });
};

export const filterResults = (list: ContactListMapItem[], searchData: ContactListPageSearchResults) => {
    const { pageSearchHits } = searchData;
    const resultIds = pageSearchHits.map((item) => item.contentLinkId);
    const newList = list.filter((item) => resultIds.includes(item.contentLinkId));

    return newList;
};

export const getUrlData = (): { catIds: number[]; searchTerm: string } => {
    const catIds = [];
    let searchTerm: string = '';
    const url = new URL(window.location.href);

    for (const [key, value] of url.searchParams.entries()) {
        if (key === 'catids') {
            if (value != null) {
                const commaSeparatedValuesOrNot = value.split(",");
                for (const val of commaSeparatedValuesOrNot) {
                    catIds.push(Number(val));
                }
            } 
        } else if (key === 'q') {
            searchTerm = value;
        }
    }

    return { catIds, searchTerm };
};

export const getSearchResults = async ({
    guuid,
    langId,
    catIds,
    searchTerm,
}: {
    guuid: string;
    langId: number;
    catIds: number[];
    searchTerm: string;
}): Promise<ContactListPageSearchResults> => {
    const mainUrl: string = window.location.origin;
    const apiUrl = `${mainUrl}/api/map/ContactListPageSearchResults/${guuid}/${langId}?catIds=${catIds.toString()}&q=${searchTerm}`;
    const data = await getData(apiUrl);
    return data;
};

const updateList = async ({
    searchTerm,
    guuid,
    langId,
    initialList,
}: {
    searchTerm: string;
    guuid: string;
    langId: number;
    initialList: ContactListMapItem[];
}) => {
    const url = new URL(window.location.href);
    const searchParams = url.searchParams;
    searchParams.delete('q');
    searchParams.append('q', searchTerm);
    url.search = searchParams.toString();
    history.pushState({}, '', url.toString());

    const { catIds } = getUrlData();
    const searchData = await getSearchResults({ guuid, langId, catIds, searchTerm });
    const filteredList = filterResults(initialList, searchData);
    searchData.pageSearchHitsCount = filteredList.length;

    const paginationEl = document.querySelector('.pagination-dynamic');
    if (paginationEl) {
        const paginationWrapperEl = paginationEl.querySelector('.pagination__wrapper');
        if (paginationWrapperEl) {
            paginationWrapperEl.innerHTML = '';
        }
    }

    cardsPagination(searchData, (pagination) => handlePageChange(pagination, filteredList));
    handleNoResultsMessage(filteredList.length < 1);
    updateTotalHits(filteredList.length);
};

const clearList = (): void => {
    const contactList = document.querySelector('.contact-list');
    if (!contactList) return;
    
    const existingItems = contactList.querySelectorAll('.contact');
    existingItems.forEach(item => item.remove());
};

const handlePageChange = (paginationData: Pagination, filteredList: ContactListMapItem[]) => {
    const { startIndex, endIndex } = paginationData;
    const slicedList = filteredList.slice(startIndex, endIndex + 1);

    clearList();
    createList(slicedList);
};

const handleNoResultsMessage = (isVisible: boolean) => {
    let noResultsDiv = document.getElementById("no-results-placeholder");
    const contactList = document.querySelector('.contact-list');
    
    if (noResultsDiv && contactList) {
        if (isVisible) {
            noResultsDiv.style.display = "block";
            (contactList as HTMLElement).style.display = "none";
        } else {
            noResultsDiv.style.display = "none";
            (contactList as HTMLElement).style.display = "";
        }
    }
}

export const updateTotalHits = (count: number) => {
    const totalHitsEl = document.querySelector('.totalHits') as HTMLElement | null;
    if (totalHitsEl) {
        const pluralText = totalHitsEl.dataset.hitsText ?? 'treff';
        const singularText = totalHitsEl.dataset.hitsTextSingular ?? pluralText;
        const hitsText = count === 1 ? singularText : pluralText;
        totalHitsEl.textContent = `${count} ${hitsText}`;
    }
}

const contactCards = async () => {
    const contactList = document.querySelector('.contact-list') as HTMLElement;
    if (!contactList) return;
    
    const inputWrapperEl = document.querySelector('.input-search-wrapper') as HTMLInputElement;
    
    // Get guuid and lang from input-search-wrapper (if filter is shown) or from contact-list (always present)
    const dataSource = inputWrapperEl || contactList;
    const { lang, guuid } = dataSource.dataset;
    
    if (!guuid || !lang) {
        console.error('Missing guuid or lang data attributes');
        return;
    }
    
    const inputEl = inputWrapperEl?.querySelector('.input-cards-filter') as HTMLInputElement;
    const iconEl = inputWrapperEl?.querySelector('.search-icon') as HTMLInputElement;
    const filterBoxEl = document.querySelector('.filter-box');
    
    const langId = Number(lang);
    const mainUrl: string = window.location.origin;
    const contactListApiUrl: string = `${mainUrl}/api/map/ContactListPageMap/${guuid}/${lang}`;

    const { catIds, searchTerm } = getUrlData();
    
    const allContactsData = await getData(contactListApiUrl);
    const searchData = await getSearchResults({ guuid: guuid || '', langId, catIds, searchTerm: searchTerm || '' });
    
    const filteredList = filterResults(allContactsData.mapItems, searchData);
    searchData.pageSearchHitsCount = filteredList.length;

    handleNoResultsMessage(filteredList.length < 1);
    updateTotalHits(filteredList.length);

    // Display initial results (first page)
    const initialPageSize = 12; // Default page size from Constants.Lists.NumberOfItemsInList
    const initialSlice = filteredList.slice(0, Math.min(initialPageSize, filteredList.length));
    createList(initialSlice);

    // Only set up search/filter handlers if the elements exist
    if (inputEl) {
        inputEl.value = searchTerm || '';
        inputEl.onkeyup = function (e) {
            const event = e as KeyboardEvent;
            if (event.key === 'Enter' && event.target) {
                const searchTerm: string = (event.target as HTMLInputElement).value;
                updateList({ searchTerm, guuid: guuid || '', langId, initialList: allContactsData.mapItems });
            }
        };
    }
    
    if (iconEl) {
        iconEl.addEventListener('click', function () {
            updateList({ searchTerm: inputEl?.value || '', guuid: guuid || '', langId, initialList: allContactsData.mapItems });
        });
    }

    if (filterBoxEl) {
        const filterSubmitButtonEl = filterBoxEl.querySelector('.filter-button--submit');
        const filterAbortButtonEl = filterBoxEl.querySelector('.filter-button--abort');

        if (filterSubmitButtonEl) {
            filterSubmitButtonEl.addEventListener('click', function () {
                updateList({ searchTerm: searchTerm || '', guuid: guuid || '', langId, initialList: allContactsData.mapItems });
            });
        }
        if (filterAbortButtonEl) {
            filterAbortButtonEl.addEventListener('click', function () {
                updateList({ searchTerm: searchTerm || '', guuid: guuid || '', langId, initialList: allContactsData.mapItems });
            });
        }
    }

    cardsPagination(searchData, (pagination) => handlePageChange(pagination, filteredList));
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', contactCards);
} else {
    contactCards();
}
